import {RdsMinimalBadgeColor} from "@rds/core";
import {Activity} from "./activity.model";

export type DeepReadOnly<T> = { readonly [key in keyof T]: DeepReadOnly<T[key]> };
export type DeepMutable<T> = { -readonly [key in keyof T]: DeepMutable<T[key]> };

export enum DatavisionStatus {
  Planned = "Planned",
  InProgress = "In Progress",
  Submitted = "Submitted",
  Accepted = "Accepted",
  Rejected = "Rejected",
  Published = "Published",
  Presented = "Presented"
}

export const STATUS_COLOR_MAP: {[key in DatavisionStatus]: RdsMinimalBadgeColor} = {
  [DatavisionStatus.Planned]: "black",
  [DatavisionStatus.InProgress]: "black",
  [DatavisionStatus.Submitted]: "yellow",
  [DatavisionStatus.Accepted]: "green",  // Changed from pink to green (like VVPM Approved)
  [DatavisionStatus.Rejected]: "red",
  [DatavisionStatus.Published]: "green",
  [DatavisionStatus.Presented]: "green"
}

export interface Study{
  sID: number | undefined
  studyNumber: string
  studyName: string
}

export const TEST_STUDY = {
  sID: 1,
  studyNumber: "Study Number",
  studyName: "Study Name"
} as Study

export interface StudyEndpointResponse {
  studies: Array<Study>
}

interface Datavision {
  // documentNumber is the primary stable identifier
  documentNumber: string
  // pID is deprecated but kept for backwards compatibility (alias for documentNumber)
  pID?: string
  title: string
  shortTitle?: string
  status: DatavisionStatus
  authors: Array<string>
  target: string
  congress_acronym?: string
  studies: Array<Study>
  expectedPublicationDate: string
  link: string
}

interface CTMS {
  cmID: string
  projectName: string
  activityName: string
  studyDescription: string
  date: string
}

export type DatavisionMetadata = DeepReadOnly<Datavision>
export type CTMSMetadata = DeepReadOnly<CTMS>

export abstract class Base {
  abstract get abstractID(): string | undefined
  abstract toPost(changes: Base): PostBase

  /**
   * This function should get overwritten by all extending classes of CreateObject
   * Injects the data from post.updatedData into an instance of the source class
   * @param post
   */
  static parseFromPostResponse(post: PostObjectResponse<any>): PostObjectResponse<any> {
    return {
      updatedData: undefined,
      success: false
    }
  }
}

export abstract class CreateObject extends Base {
  abstract get abstractDate(): string
}

export abstract class DataObject extends CreateObject {
  abstract override get abstractID(): string
}

export const LIST_REMOVE_FLAG = "#REMOVE_KEYWORD#"
export const LIST_ADD_FLAG = "#ADD_KEWOD#"
export type Keywords = Array<string>

export abstract class PostBase {
  dataChanged!: boolean

  protected constructor(source: Base, newValue: Base) {
    /* The type conversion is necessary because the compiler does not recognize that, e.g.,
     * an ActivityPost is calling this constructor with an Activity as source and newValue parameters.
     * The type conversion does not change the content of the keys array
     */
    this.dataChanged = this.checkChanges(Object.keys(source) as (keyof Base)[], source, newValue)
  }

  abstract getSourceWrapper(): (post: PostObjectResponse<any>) => PostObjectResponse<any>

  private checkChanges<T extends object>(keys: Array<keyof T>, source: T, newValue: T): boolean {
    for (let key of keys) {
      const sourceValue = source[key]!;
      const newValueValue = newValue[key]!;

      if (sourceValue === null || newValueValue === null) {
        if (sourceValue !== newValueValue) {
          return true;
        }
      } else if (Array.isArray(sourceValue) && Array.isArray(newValueValue)) {
        if (this.compareArrays(sourceValue, newValueValue)) {
          return true;
        }
      } else if (typeof sourceValue === 'object' && typeof newValueValue === 'object') {
        // @ts-ignore
        if (this.checkChanges(Object.keys(sourceValue) as (keyof T)[], sourceValue, newValueValue)) {
          return true;
        }
      } else if (sourceValue !== newValueValue) {
        return true;
      }
    }
    return false;
  }

  private compareArrays(arr1: any[], arr2: any[]): boolean {
    if (arr1.length !== arr2.length) {
      return true;
    }
    for (let i = 0; i < arr1.length; i++) {
      if (arr1[i] === null || arr2[i] === null) {
        if (arr1[i] !== arr2[i]) {
          return true;
        }
      } else if (Array.isArray(arr1[i]) && Array.isArray(arr2[i])) {
        if (this.compareArrays(arr1[i], arr2[i])) {
          return true;
        }
      } else if (typeof arr1[i] === 'object' && typeof arr2[i] === 'object') {
        if (this.checkChanges(Object.keys(arr1[i]) as (keyof any)[], arr1[i], arr2[i])) {
          return true;
        }
      } else if (arr1[i] !== arr2[i]) {
        return true;
      }
    }
    return false;
  }
}

export abstract class PostObject extends PostBase {
  keyWords!: Keywords

  protected constructor(source: Base & {keyWords: Keywords}, newValue: Base & {keyWords: Keywords}) {
    super(source, newValue)
    this.keyWords = this.flagDifferencesInArray(source.keyWords, newValue.keyWords)
  }

  flagDifferencesInArray(source: Array<string>, newValues: Array<string>): Keywords {
    const resArr = []
    const union = new Set([...source, ...newValues])
    for(let value of union){
      if(!source.includes(value)) {
        resArr.push(LIST_ADD_FLAG + value)
      }
      else if(!newValues.includes(value)) {
        resArr.push(LIST_REMOVE_FLAG + value)
      }
    }
    return resArr
  }
}

export class PostObjectResponse<T extends Base>{
  success!: boolean
  updatedData!: T | undefined
}
